import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media.
 * @property {HTMLElement} videoControls - The video controls container.
 * @property {HTMLInputElement} progressInput - The progress bar input.
 * @property {HTMLElement} progressFill - The progress bar fill element.
 * @property {HTMLElement} progressThumb - The progress bar thumb element.
 * @property {HTMLElement} timestamp - The timestamp element.
 * @property {HTMLElement} muteButton - The mute button.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {boolean} */
  isPlaying = false;

  /** @type {boolean} */
  isMuted = true;

  /** @type {number | null} */
  #progressInterval = null;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    // Enforce custom controls-only UI regardless of browser defaults.
    videoElement.controls = false;
    videoElement.removeAttribute('controls');

    videoElement.load();
    this.#bindVideoEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.stopProgressTracking();
  }

  #bindVideoEvents() {
    const { videoElement } = this.refs;
    if (!videoElement) return;

    const signal = this.#abortController.signal;

    videoElement.addEventListener(
      'play',
      () => {
        this.isPlaying = true;
        this.updatePlayPauseHint(this.isPlaying);
        this.startProgressTracking();
      },
      { signal }
    );

    videoElement.addEventListener(
      'pause',
      () => {
        this.isPlaying = false;
        this.updatePlayPauseHint(this.isPlaying);
        this.stopProgressTracking();
      },
      { signal }
    );

    videoElement.addEventListener(
      'loadedmetadata',
      () => {
        this.isMuted = videoElement.muted;
        this.updateMuteHint(this.isMuted);
        this.updateProgress();
        this.updatePlayPauseHint(!videoElement.paused);
      },
      { signal }
    );
  }

  /**
   * Updates the visual hint for play/pause state.
   * @param {boolean} isPlaying - Whether the video is currently playing.
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon__play');
      const pauseIcon = toggleMediaButton.querySelector('.icon__pause');
      const playText = toggleMediaButton.querySelector('.video-player__play-text');
      const pauseText = toggleMediaButton.querySelector('.video-player__pause-text');

      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
      if (playText) playText.classList.toggle('hidden', isPlaying);
      if (pauseText) pauseText.classList.toggle('hidden', !isPlaying);
    }

    const videoControls = this.refs.videoControls;
    if (videoControls instanceof HTMLElement) {
      videoControls.classList.remove('hidden');
    }
  }

  /**
   * Updates the mute button visual state.
   * @param {boolean} isMuted - Whether the video is currently muted.
   */
  updateMuteHint(isMuted) {
    const muteButton = this.refs.muteButton;
    if (muteButton instanceof HTMLElement) {
      const unmuteIcon = muteButton.querySelector('.icon__unmute');
      const muteIcon = muteButton.querySelector('.icon__mute');
      const unmuteText = muteButton.querySelector('.video-player__unmute-text');
      const muteText = muteButton.querySelector('.video-player__mute-text');

      if (unmuteIcon) unmuteIcon.classList.toggle('hidden', !isMuted);
      if (muteIcon) muteIcon.classList.toggle('hidden', isMuted);
      if (unmuteText) unmuteText.classList.toggle('hidden', !isMuted);
      if (muteText) muteText.classList.toggle('hidden', isMuted);
    }
  }

  /**
   * Formats seconds to MM:SS.
   * @param {number} seconds
   * @returns {string}
   */
  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Updates the progress bar.
   */
  updateProgress() {
    const { videoElement } = this.refs;
    if (!videoElement) return;

    const progress = (videoElement.currentTime / videoElement.duration) * 100 || 0;
    const { progressInput, progressFill, progressThumb, timestamp } = this.refs;

    if (progressInput) progressInput.value = String(progress);
    if (progressFill) progressFill.style.width = `${progress}%`;
    if (progressThumb) progressThumb.style.left = `${progress}%`;
    if (timestamp) timestamp.textContent = this.formatTime(videoElement.currentTime);
  }

  /**
   * Starts tracking video progress.
   */
  startProgressTracking() {
    this.stopProgressTracking();
    this.#progressInterval = window.setInterval(() => {
      this.updateProgress();
    }, 100);
  }

  /**
   * Stops tracking video progress.
   */
  stopProgressTracking() {
    if (this.#progressInterval) {
      clearInterval(this.#progressInterval);
      this.#progressInterval = null;
    }
  }

  /**
   * Seeks the video to a specific position.
   */
  seekVideo() {
    const { videoElement, progressInput } = this.refs;
    if (!videoElement || !progressInput) return;

    const seekTime = (parseFloat(progressInput.value) / 100) * videoElement.duration;
    videoElement.currentTime = seekTime;
    this.updateProgress();
  }

  /**
   * Toggles mute state.
   */
  toggleMute() {
    const { videoElement } = this.refs;
    if (!videoElement) return;

    this.isMuted = !this.isMuted;
    videoElement.muted = this.isMuted;
    this.updateMuteHint(this.isMuted);
  }

  /**
   * Toggles play/pause state.
   */
  toggleMedia() {
    const { videoElement } = this.refs;
    if (!videoElement) return;

    if (videoElement.paused) {
      videoElement.play();
      return;
    }

    videoElement.pause();
  }

  /**
   * Stops event propagation to prevent parent interactions.
   * @param {Event} event
   */
  stopPropagation = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
